import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, of, Subscription } from 'rxjs';
import { TranslationManagerService } from '../../../../../core/i18n';
import { FUNNEL_TYPE } from '../../../../../enum/constants';
import { TaskService } from '../../../../../services/task.service';
import { ResolvedTaskLink, Task, TaskUser } from '../../../../tasks/tasks.types';
import { isTaskFinished } from '../../../../tasks/tasks.utils';

/**
 * The tasks linked to a card: a lead or a deal. Each opens in a sidebar over the card, and a new one is created
 * with the link to the card already in place.
 */
@Component({
  selector: 'app-board-v3-card-tasks-panel',
  templateUrl: './card-tasks-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3CardTasksPanelComponent implements OnInit, OnChanges {
  @Input({ required: true }) cardId!: string;
  /** The name shown on the link of a task created here */
  @Input() cardName = '';
  /** Tells a lead from a deal on that link */
  @Input() funnelType: FUNNEL_TYPE | null = null;
  /** How many tasks the card has, for the counter of the tab */
  @Output() totalChange = new EventEmitter<number>();

  private readonly taskService = inject(TaskService);
  private readonly translationManager = inject(TranslationManagerService);
  private readonly destroyRef = inject(DestroyRef);

  protected tasks: Task[] = [];
  protected loading = true;
  protected failed = false;
  protected readonly isTaskFinished = isTaskFinished;

  private request?: Subscription;
  private usersById = new Map<string, TaskUser>();

  ngOnInit(): void {
    // The card opens over any screen, not only over the ones whose route loads the tasks texts
    this.translationManager.loadModuleTranslations('tasks').pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.taskService
      .getUsers()
      .pipe(
        catchError(() => of([] as TaskUser[])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((users) => (this.usersById = new Map(users.map((user) => [user.id, user]))));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cardId'] && this.cardId) this.load();
  }

  protected getUser(id: string): TaskUser | null {
    return this.usersById.get(id) ?? null;
  }

  protected load(showLoader = true): void {
    const cardId = this.cardId;
    this.request?.unsubscribe();
    this.loading = showLoader;
    this.failed = false;
    this.request = this.taskService
      .getTasks({ entity_type: 'card', entity_id: cardId, take: 100 })
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ items, total }) => {
          this.tasks = items;
          this.totalChange.emit(total);
        },
        error: (error) => {
          this.failed = true;
          this.taskService.error(error);
        },
      });
  }

  /** The form opens with the link to this card, so the task is about it from the start */
  protected create(): void {
    const link: ResolvedTaskLink = {
      entity_type: 'card',
      entity_id: this.cardId,
      name: this.cardName,
      funnelType: this.funnelType ?? undefined,
    };
    this.taskService
      .openTaskForm({ links: [link] })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((task) => task && this.load(false));
  }

  protected open(task: Task): void {
    this.taskService
      .openTaskDetail(task.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => result?.changed && this.load(false));
  }
}
